import { Button } from './button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './card';
import { AlertCircle } from 'lucide-react';

interface SentryErrorFallbackProps {
  error: unknown;
  resetError: () => void;
  eventId?: string;
}

export function SentryErrorFallback({ error, resetError, eventId }: SentryErrorFallbackProps) {
  const isDevelopment = import.meta.env.DEV;
  const errorMessage =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-neutral-50 dark:bg-neutral-950">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="size-6 text-red-500" />
            <CardTitle>알 수 없는 오류가 발생했습니다</CardTitle>
          </div>
          <CardDescription>
            애플리케이션에서 예상치 못한 오류가 발생했습니다. 문제가 지속되면 고객 지원팀에 문의해주세요.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {eventId && (
            <div className="p-3 rounded-md bg-neutral-100 dark:bg-neutral-900">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                <strong>오류 ID:</strong> {eventId}
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                이 ID를 고객 지원팀에 전달해주시면 더 빠르게 문제를 해결할 수 있습니다.
              </p>
            </div>
          )}

          {isDevelopment && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
                개발 모드 - 오류 상세 정보:
              </p>
              <pre className="text-xs text-red-800 dark:text-red-300 overflow-auto max-h-48 whitespace-pre-wrap">
                {errorMessage}
                {errorStack && `\n\n${errorStack}`}
              </pre>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button onClick={resetError} variant="default">
            다시 시도
          </Button>
          <Button
            onClick={() => {
              window.location.href = '/';
            }}
            variant="outline"
          >
            홈으로 이동
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

